import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, FlaskConical, Globe, Sparkles } from 'lucide-react';

import { CountryMultiSelect } from '@/components/country-multi-select';
import { countries } from '@/data/countries';

import { WizardOptionCard } from './WizardOptionCard';
import type { OfferStrategy, QuickstartAnswers } from './types';

interface StepOfferStrategyProps {
    answers: QuickstartAnswers;
    onChange: (next: Partial<QuickstartAnswers>) => void;
}

const ALL_COUNTRY_CODES = countries.map((c) => c['alpha-2']);

export function StepOfferStrategy({ answers, onChange }: StepOfferStrategyProps) {
    const { t } = useTranslation('common');
    const select = (value: OfferStrategy) => {
        // Reset geoCountries when switching away from geo
        onChange({
            offerStrategy: value,
            geoCountries: value === 'geo' ? answers.geoCountries : [],
        });
    };

    // Preselect the first option ("No upsell page") on mount.
    React.useEffect(() => {
        if (answers.offerStrategy === null) {
            onChange({ offerStrategy: 'none', geoCountries: [] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h3 className="text-lg font-semibold">
                    {t('funnelQuickstart.offerStrategy.title')}
                </h3>
                <p className="text-muted-foreground text-sm">
                    {t('funnelQuickstart.offerStrategy.subtitle')}
                </p>
            </div>

            <div className="flex flex-col gap-3">
                <WizardOptionCard
                    title={t('funnelQuickstart.offerStrategy.none.title')}
                    description={t('funnelQuickstart.offerStrategy.none.description')}
                    icon={<Ban className="h-5 w-5" />}
                    selected={answers.offerStrategy === 'none'}
                    onSelect={() => select('none')}
                />
                <WizardOptionCard
                    title={t('funnelQuickstart.offerStrategy.single.title')}
                    description={t('funnelQuickstart.offerStrategy.single.description')}
                    icon={<Sparkles className="h-5 w-5" />}
                    selected={answers.offerStrategy === 'single'}
                    onSelect={() => select('single')}
                />
                <WizardOptionCard
                    title={t('funnelQuickstart.offerStrategy.geo.title')}
                    description={t('funnelQuickstart.offerStrategy.geo.description')}
                    icon={<Globe className="h-5 w-5" />}
                    selected={answers.offerStrategy === 'geo'}
                    onSelect={() => select('geo')}
                />
                {answers.offerStrategy === 'geo' && (
                    <div className="ml-4 flex flex-col gap-2 border-l-2 border-primary/30 pl-4 py-2">
                        <p className="text-sm font-medium">
                            {t('funnelQuickstart.offerStrategy.geo.pickerTitle')}
                        </p>
                        <CountryMultiSelect
                            countryCodes={ALL_COUNTRY_CODES}
                            selected={answers.geoCountries}
                            onChange={(selected) => onChange({ geoCountries: selected })}
                            placeholder={t('funnelQuickstart.offerStrategy.geo.pickerPlaceholder')}
                        />
                        <p className="text-muted-foreground text-xs">
                            {t('funnelQuickstart.offerStrategy.geo.pickerHelp')}
                        </p>
                    </div>
                )}
                <WizardOptionCard
                    title={t('funnelQuickstart.offerStrategy.split.title')}
                    description={t('funnelQuickstart.offerStrategy.split.description')}
                    icon={<FlaskConical className="h-5 w-5" />}
                    selected={answers.offerStrategy === 'split'}
                    onSelect={() => select('split')}
                />
            </div>
        </div>
    );
}
